import React, { memo, useMemo, ChangeEvent } from 'react'
import { Checkbox } from 'components/forms/Checkbox'
import { SelectBase } from 'components/forms/Selects/SelectBase'
import { ISelectOption } from 'components/forms/Selects/SelectOption'
import uuid from 'uuid/v4'

interface IDisplayValueProps {
  values: ISelectOption[]
  placeholder: string
}

export const DisplayValue = memo<IDisplayValueProps>(({ placeholder, values }) => (
  <span
    className='multi-select-display-value flex items-center'
    data-test='multi-select-display-value'>
    <span
      className={`flex-grow-1 ellipsis ${values.length ? '' : 'text-secondary'}`}
      data-test='multi-select-values-text'>
      {values.map(item => item.label).join(', ') || placeholder}
    </span>
    {values.length > 1 && (
      <span className='tag tag-round tag-small' data-test='multi-select-values-count'>
        {values.length}
      </span>
    )}
  </span>
))

export interface IInputMultiSelectProps {
  id?: string
  label?: string
  placeholder?: string
  dataTest?: string
  options: ISelectOption[]
  values?: (string | number | ISelectOption)[]
  onChange?(options: ISelectOption[], id: string): void
  onClose?: () => void
  disabled?: boolean
  invalid?: boolean
  validationMessage?: string
  large?: boolean
  required?: boolean
  /**
   * @default true
   *
   * When true, adds the "clear all" option at the bottom of the multiselect
   */
  clearable?: boolean
}

export const InputMultiSelect = memo<IInputMultiSelectProps>(
  ({
    id = uuid(),
    label: selectLabel,
    placeholder = 'Choose below',
    dataTest = `multi-select-${id}`,
    values = [],
    options,
    onChange,
    onClose,
    disabled,
    required,
    invalid,
    validationMessage,
    large,
    clearable = true,
  }) => {
    const find = (
      value: string | number,
      list: ISelectOption[] = options
    ): ISelectOption | undefined => list.find(item => String(item.value) === String(value))

    const normalizedValues = useMemo(
      () =>
        values
          .map(value => {
            if (typeof value === 'string' || typeof value === 'number') return find(value)
            return value
            // eslint-disable-next-line react-hooks/exhaustive-deps
          })
          .filter(Boolean) as ISelectOption[],
      [options, values]
    )

    const isSelected = (value: string | number) => !!find(value, normalizedValues)
    const clearAllHandler = clearable ? () => onChange && onChange([], id) : undefined
    const handleChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      const newValues = isSelected(value)
        ? normalizedValues.filter(({ value: itemValue }) => String(itemValue) !== value)
        : options.filter(
            ({ value: itemValue }) =>
              String(itemValue) === value || !!find(itemValue, normalizedValues)
          )
      if (onChange) onChange(newValues, id)
    }

    return (
      <SelectBase
        id={id}
        label={selectLabel}
        dataTest={dataTest}
        onClear={clearAllHandler}
        onClose={onClose}
        displayValue={<DisplayValue values={normalizedValues} placeholder={placeholder} />}
        disabled={disabled}
        required={required}
        invalid={invalid}
        validationMessage={validationMessage}
        large={large}
        selectButtonType='select-display-value'>
        {options.map(({ label, value }) => (
          <Checkbox
            key={value}
            id={`select-${id}-option-${value}`}
            dataTest={`${dataTest}-${value}`}
            checked={isSelected(value)}
            label={label}
            value={String(value)}
            onChange={handleChange}
            role='option'
            aria-selected={isSelected(value)}
          />
        ))}
      </SelectBase>
    )
  }
)

export default InputMultiSelect
